<template>
  <RegionBasedAnnotationContainer
    :interactive="!isReadOnly && canPersist"
    :task-id="currentTaskId"
    :effective-mpp-x="measurementMetadata?.x"
    :effective-mpp-y="measurementMetadata?.y"
  />
  <BModal
    id="rbc-save-conflict-modal"
    :model-value="hasSaveConflict"
    title="Annotations changed"
    no-close-on-backdrop
    no-close-on-esc
    no-header-close
  >
    <p>
      This task was updated in another session, deleted, or is no longer available to you. Reload
      the task before continuing.
    </p>
    <p class="mb-0">
      Your unsaved changes on this page will be discarded.
    </p>
    <p
      v-if="reloadConflictFailed"
      class="mt-3 mb-0 text-danger"
      role="alert"
    >
      The task could not be reloaded. Check your connection and try again.
    </p>
    <template #footer>
      <BButton
        class="reload-task-button"
        variant="primary"
        :disabled="isReloadingConflict"
        @click="reloadTask"
      >
        {{ isReloadingConflict ? 'Reloading...' : 'Reload task' }}
      </BButton>
    </template>
  </BModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, provide, ref, watch } from 'vue';
import { BButton, BModal } from 'bootstrap-vue-next';
import RegionBasedAnnotationContainer from '@/features/RegionBasedAnnotation/RegionBasedAnnotationContainer.vue';
import { useConfigStore, useMeasurementRulerStore } from '@/store';
import { useRBCSlideStore } from '@/store/modules/RBCregion';
import { useConstants, useApi, useCookie } from '@/composables/useAppGlobals';
import { useRBC } from '@/features/RegionBasedAnnotation/useRBC';
import { reportReviewInjectionKey } from '@/views/components/reportReviewContext';
import { isTaskReadOnly } from '@/utils/taskAccess';

const config = useConfigStore();
const isReadOnly = computed(() => config.isReadOnly);
const slideStore = useRBCSlideStore();
const $const = useConstants();
const $api = useApi();
const $cookie = useCookie();
const rulerStore = useMeasurementRulerStore();
let loadToken = 0;
const loadedTaskScope = ref<string | null>(null);
const loadedSessionGeneration = ref<number | null>(null);
const loadedRequestGeneration = ref<number | null>(null);
const snapshotVersion = ref<number | null>(null);
const taskInstanceId = ref<string | null>(null);
const isReloadingConflict = ref(false);
const reloadConflictFailed = ref(false);

const slideInfo = computed(() => config.slideInfo);
const currentTaskId = computed(() => {
  const value = slideInfo.value[$const.SLIDE_KEYS.SLIDE_ID];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
});
const currentTaskScope = computed(() => {
  if (!currentTaskId.value) return null;
  const project = $api.utils.getProjectId() || $api.utils.getProject() || 'unscoped';
  return `${project}:${currentTaskId.value}`;
});
const persistenceScope = computed(() => {
  if (
    !currentTaskScope.value
    || loadedTaskScope.value !== currentTaskScope.value
    || loadedSessionGeneration.value !== rulerStore.sessionGeneration
    || loadedRequestGeneration.value === null
    || isReadOnly.value
  ) return null;
  return `${currentTaskScope.value}:${loadedRequestGeneration.value}`;
});
const reviewContext = useRBC(persistenceScope, snapshotVersion, taskInstanceId);
const { canPersist, hasSaveConflict } = reviewContext;
provide(reportReviewInjectionKey, reviewContext);
const measurementMetadata = computed(() =>
  currentTaskId.value ? rulerStore.metadata(currentTaskId.value) : null,
);

async function loadRBCImages(
  slideId: string,
  taskScope: string,
  token: number,
  sessionGeneration: number,
): Promise<boolean> {
  try {
    const rawResponse = await $api.getRBCImages(slideId);
    if (
      !rawResponse
      || typeof rawResponse.task_instance_id !== 'string'
      || !rawResponse.task_instance_id
      || token !== loadToken
      || currentTaskScope.value !== taskScope
      || sessionGeneration !== rulerStore.sessionGeneration
    ) return false;

    rulerStore.setMetadata(
      slideId,
      rawResponse.effective_mpp_x,
      rawResponse.effective_mpp_y,
    );
    if (typeof rawResponse.state === 'string') {
      config.updateSlideInfoKey($const.SLIDE_KEYS.STATE, rawResponse.state);
    }
    config.updateSlideInfoKey($const.SLIDE_KEYS.ASSIGNEE, rawResponse.assignee);
    config.updateSlideInfoKey($const.SLIDE_KEYS.ASSIGNEE_ID, rawResponse.assignee_id);
    config.setIsReadOnly(isTaskReadOnly(
      rawResponse,
      {
        id: $api.utils.getUserId(),
        name: $cookie.getCookie($const.COOKIES.KEY.USER_NAME),
      },
      $const.TASK_STATES.DONE,
    ));
    if (rawResponse.reviewer !== undefined) {
      config.updateSlideInfoKey($const.SLIDE_KEYS.REVIEWER, rawResponse.reviewer);
    }
    if (rawResponse.last_check_time !== undefined) {
      config.updateSlideInfoKey($const.SLIDE_KEYS.REVIEW_TIME, rawResponse.last_check_time);
    }

    const schema = rawResponse.cells;
    Object.values(schema).forEach((image) => {
      if (image?.image_url && !image.image_url.startsWith('http')) {
        image.image_url = $api.getRBCImageUrl(image.image_url);
      }
    });
    slideStore.loadSchema(schema);
    snapshotVersion.value = rawResponse.snapshot_version;
    taskInstanceId.value = rawResponse.task_instance_id;
    loadedTaskScope.value = taskScope;
    loadedSessionGeneration.value = sessionGeneration;
    loadedRequestGeneration.value = token;
    reviewContext.clearSaveConflict();
    return true;
  } catch (error) {
    console.error('Failed to load RBC images', error);
    return false;
  }
}

async function reloadTask() {
  const taskId = currentTaskId.value;
  const taskScope = currentTaskScope.value;
  if (!taskId || !taskScope || isReloadingConflict.value) return;

  loadToken += 1;
  const token = loadToken;
  const sessionGeneration = rulerStore.sessionGeneration;
  loadedTaskScope.value = null;
  loadedSessionGeneration.value = null;
  loadedRequestGeneration.value = null;
  snapshotVersion.value = null;
  taskInstanceId.value = null;
  reloadConflictFailed.value = false;
  isReloadingConflict.value = true;
  try {
    const loaded = await loadRBCImages(taskId, taskScope, token, sessionGeneration);
    if (!loaded && (
      currentTaskScope.value === taskScope
      && rulerStore.sessionGeneration === sessionGeneration
    )) {
      reloadConflictFailed.value = true;
    }
  } finally {
    isReloadingConflict.value = false;
  }
}

watch(hasSaveConflict, (hasConflict) => {
  if (!hasConflict) return;
  loadedTaskScope.value = null;
  loadedSessionGeneration.value = null;
  loadedRequestGeneration.value = null;
  snapshotVersion.value = null;
  taskInstanceId.value = null;
});

watch(
  currentTaskScope,
  (taskScope) => {
    reloadConflictFailed.value = false;
    loadToken += 1;
    loadedTaskScope.value = null;
    loadedSessionGeneration.value = null;
    loadedRequestGeneration.value = null;
    snapshotVersion.value = null;
    taskInstanceId.value = null;
    if (!taskScope || !currentTaskId.value) {
      slideStore.reset();
      return;
    }
    const taskId = currentTaskId.value;
    slideStore.reset();
    rulerStore.clearMetadata(taskId);
    void loadRBCImages(
      taskId,
      taskScope,
      loadToken,
      rulerStore.sessionGeneration,
    );
  },
  { immediate: true },
);

watch(
  () => rulerStore.sessionGeneration,
  () => {
    reloadConflictFailed.value = false;
    loadToken += 1;
    loadedTaskScope.value = null;
    loadedSessionGeneration.value = null;
    loadedRequestGeneration.value = null;
    snapshotVersion.value = null;
    taskInstanceId.value = null;
    slideStore.reset();
  },
);

onBeforeUnmount(() => {
  loadToken += 1;
  loadedTaskScope.value = null;
  loadedSessionGeneration.value = null;
  loadedRequestGeneration.value = null;
  snapshotVersion.value = null;
  taskInstanceId.value = null;
  slideStore.reset();
});
</script>

<style scoped>
.reload-task-button {
  min-width: 8rem;
}
</style>
